import { useMemo, useState } from 'react';
import {
  AdvertisementPlatform, AdvertisementType, AdvertisementSlot, AdvertisementMediaType,
  PAGE_NAMES, SLOT_NAMES, labelFor,
} from '../adConstants';
import { getWebSections, getMobileSections } from './previewSections';

const MediaPreview = ({ url, mediaType }) => {
  if (!url) return null;
  if (mediaType === AdvertisementMediaType.Video) {
    return (
      <div className="ap-promo-media ap-promo-media-video">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
      </div>
    );
  }
  return (
    <div className="ap-promo-media">
      <img src={url} alt="" />
    </div>
  );
};

const AdBlock = ({ ad, tall }) => (
  <div className={`ap-promo-block ${tall ? 'ap-block-tall' : ''}`}>
    <span className="ap-promo-tag">{ad.rtl ? 'سيظهر الإعلان هنا' : 'Advertisement will appear here'}</span>
    <MediaPreview url={ad.mediaUrl} mediaType={ad.mediaType} />
    {(ad.title || ad.description) && (
      <div className="ap-promo-text">
        {ad.title && <div className="ap-promo-title">{ad.title}</div>}
        {ad.description && <div className="ap-promo-desc">{ad.description}</div>}
      </div>
    )}
    {ad.showCta && ad.ctaText && <span className="ap-promo-cta">{ad.ctaText}</span>}
  </div>
);

const SectionList = ({ sections, activeSlot, ad, dimmed }) => (
  <div className={`ap-sections ${dimmed ? 'ap-dimmed' : ''}`}>
    {sections.map((s) => {
      const showBefore = s.beforeSlot !== undefined && s.beforeSlot === activeSlot;
      const showAfter = s.afterSlot !== undefined && s.afterSlot === activeSlot;
      const replace = s.slot !== undefined && s.slot === activeSlot;
      return (
        <div key={s.id}>
          {showBefore && <AdBlock ad={ad} />}
          {replace && <AdBlock ad={ad} tall={s.tall} />}
          {!replace && !s.spacer && (
            <div className={`ap-block ${s.tall ? 'ap-block-tall' : ''}`}>
              <span className="ap-block-label">{ad.rtl ? s.ar : (s.en || s.ar)}</span>
            </div>
          )}
          {showAfter && <AdBlock ad={ad} />}
        </div>
      );
    })}
  </div>
);

const PopupCard = ({ ad }) => (
  <div className="ap-popup-card">
    <span className="ap-popup-close">×</span>
    <MediaPreview url={ad.mediaUrl} mediaType={ad.mediaType} />
    <div className="ap-popup-body">
      {ad.title && <div className="ap-popup-title">{ad.title}</div>}
      {ad.description && <div className="ap-popup-desc">{ad.description}</div>}
      {ad.showCta && ad.ctaText && <span className="ap-promo-cta">{ad.ctaText}</span>}
    </div>
  </div>
);

const NewsBarBar = ({ ad, scrolling }) => (
  <div className="ap-newsbar">
    <div className={`ap-newsbar-track ${scrolling ? 'ap-marquee' : ''}`}>
      <span>{ad.title || ad.description || (ad.rtl ? 'نص الشريط الإخباري' : 'News bar text')}</span>
      {ad.showCta && ad.ctaText && <strong className="ap-newsbar-cta"> · {ad.ctaText}</strong>}
    </div>
  </div>
);

const BrowserChrome = ({ children, dir }) => (
  <div className="ap-browser">
    <div className="ap-browser-bar">
      <span className="ap-dot ap-dot-red" />
      <span className="ap-dot ap-dot-yellow" />
      <span className="ap-dot ap-dot-green" />
      <div className="ap-address">rimalalkhalij.com</div>
    </div>
    <div className="ap-browser-body" dir={dir}>{children}</div>
  </div>
);

const PhoneFrame = ({ children }) => (
  <div className="ap-phone">
    <div className="ap-phone-notch" />
    <div className="ap-phone-status">
      <span>9:41</span>
      <span>●●●</span>
    </div>
    <div className="ap-phone-body" dir="rtl">{children}</div>
    <div className="ap-phone-tabbar">
      <span className="ap-phone-tab" />
      <span className="ap-phone-tab" />
      <span className="ap-phone-tab" />
      <span className="ap-phone-tab" />
    </div>
  </div>
);

function renderWebContent(type, placement, ad, newsBarIsScrolling) {
  if (!placement) return null;
  const sections = getWebSections(placement.page);
  if (type === AdvertisementType.Popup) {
    return (
      <div className="ap-popup-wrap">
        <div className="ap-chrome-header" />
        <SectionList sections={sections} activeSlot={null} ad={ad} dimmed />
        <div className="ap-chrome-footer" />
        <div className="ap-popup-overlay">
          <PopupCard ad={ad} />
        </div>
      </div>
    );
  }
  if (type === AdvertisementType.NewsBar) {
    return (
      <>
        {placement.slot === AdvertisementSlot.AboveHeader && <NewsBarBar ad={ad} scrolling={newsBarIsScrolling} />}
        <div className="ap-chrome-header" />
        {placement.slot === AdvertisementSlot.BelowHeader && <NewsBarBar ad={ad} scrolling={newsBarIsScrolling} />}
        <div className="ap-sections">
          {sections.slice(0, 3).filter((s) => !s.spacer).map((s) => (
            <div key={s.id} className={`ap-block ${s.tall ? 'ap-block-tall' : ''}`}>
              <span className="ap-block-label">{ad.rtl ? s.ar : (s.en || s.ar)}</span>
            </div>
          ))}
        </div>
        {placement.slot === AdvertisementSlot.AboveFooter && <NewsBarBar ad={ad} scrolling={newsBarIsScrolling} />}
        <div className="ap-chrome-footer" />
      </>
    );
  }
  return (
    <>
      <div className="ap-chrome-header" />
      <SectionList sections={sections} activeSlot={placement.slot} ad={ad} />
      <div className="ap-chrome-footer" />
    </>
  );
}

const MobileAppBar = () => (
  <div className="ap-mobile-appbar">
    <span className="ap-mobile-logo" />
    <span className="ap-mobile-title">رمال الخليج</span>
  </div>
);

function renderMobileContent(type, placement, ad, newsBarIsScrolling) {
  if (!placement) return null;
  const sections = getMobileSections(placement.page);
  if (type === AdvertisementType.Popup) {
    return (
      <div className="ap-popup-wrap">
        <MobileAppBar />
        <div className="ap-mobile-search" />
        <SectionList sections={sections} activeSlot={null} ad={ad} dimmed />
        <div className="ap-popup-overlay">
          <PopupCard ad={ad} />
        </div>
      </div>
    );
  }
  if (type === AdvertisementType.NewsBar) {
    return (
      <>
        {placement.slot === AdvertisementSlot.AboveHeader && <NewsBarBar ad={ad} scrolling={newsBarIsScrolling} />}
        <MobileAppBar />
        {placement.slot === AdvertisementSlot.BelowHeader && <NewsBarBar ad={ad} scrolling={newsBarIsScrolling} />}
        <div className="ap-sections">
          {sections.slice(0, 3).filter((s) => !s.spacer).map((s) => (
            <div key={s.id} className={`ap-block ${s.tall ? 'ap-block-tall' : ''}`}>
              <span className="ap-block-label">{s.ar}</span>
            </div>
          ))}
        </div>
        {placement.slot === AdvertisementSlot.AboveFooter && <NewsBarBar ad={ad} scrolling={newsBarIsScrolling} />}
      </>
    );
  }
  return (
    <>
      <MobileAppBar />
      <div className="ap-mobile-search" />
      <SectionList sections={sections} activeSlot={placement.slot} ad={ad} />
    </>
  );
}

/**
 * Two-tab (Web / Mobile) live preview. Renders only from local form state — no
 * server round-trip. Tabs only appear for platforms that currently have at
 * least one placement.
 */
const AdvertisementPreview = ({
  type,
  placements,
  mediaType,
  mediaPreviewUrl,
  titleEn, titleAr, descriptionEn, descriptionAr,
  showCta, ctaTextEn, ctaTextAr,
  newsBarIsScrolling,
  isRtl,
}) => {
  const webPlacements = useMemo(() => (placements || []).filter((p) => p.platform === AdvertisementPlatform.Web), [placements]);
  const mobilePlacements = useMemo(() => (placements || []).filter((p) => p.platform === AdvertisementPlatform.Mobile), [placements]);

  const hasWeb = webPlacements.length > 0;
  const hasMobile = mobilePlacements.length > 0;

  const [activeTab, setActiveTab] = useState(null);
  const [webLang, setWebLang] = useState(isRtl ? 'ar' : 'en');
  const [webIndex, setWebIndex] = useState(0);
  const [mobileIndex, setMobileIndex] = useState(0);

  const tab = activeTab && ((activeTab === 'web' && hasWeb) || (activeTab === 'mobile' && hasMobile))
    ? activeTab
    : (hasWeb ? 'web' : (hasMobile ? 'mobile' : null));

  const webPlacement = webPlacements[Math.min(webIndex, Math.max(webPlacements.length - 1, 0))];
  const mobilePlacement = mobilePlacements[Math.min(mobileIndex, Math.max(mobilePlacements.length - 1, 0))];

  const webAd = {
    title: webLang === 'ar' ? titleAr : titleEn,
    description: webLang === 'ar' ? descriptionAr : descriptionEn,
    ctaText: webLang === 'ar' ? ctaTextAr : ctaTextEn,
    showCta,
    mediaUrl: mediaPreviewUrl,
    mediaType,
    rtl: webLang === 'ar',
  };
  const mobileAd = {
    title: titleAr || titleEn,
    description: descriptionAr || descriptionEn,
    ctaText: ctaTextAr || ctaTextEn,
    showCta,
    mediaUrl: mediaPreviewUrl,
    mediaType,
    rtl: true,
  };

  return (
    <div className="promo-preview-panel">
      <div className="promo-preview-tabs">
        {hasWeb && (
          <button type="button" className={tab === 'web' ? 'active' : ''} onClick={() => setActiveTab('web')}>
            {isRtl ? 'معاينة الويب' : 'Web Preview'}
          </button>
        )}
        {hasMobile && (
          <button type="button" className={tab === 'mobile' ? 'active' : ''} onClick={() => setActiveTab('mobile')}>
            {isRtl ? 'معاينة الجوال' : 'Mobile Preview'}
          </button>
        )}
        {!hasWeb && !hasMobile && (
          <span className="promo-preview-tabs-empty">
            {isRtl ? 'أضف موضعاً لمعاينة الصفحة الفعلية' : 'Add a placement to preview the actual page'}
          </span>
        )}
      </div>

      {tab === 'web' && (
        <div className="promo-preview-body">
          {webPlacements.length > 1 && (
            <div className="promo-preview-placement-switch">
              {webPlacements.map((p, i) => (
                <button key={i} type="button" className={i === webIndex ? 'active' : ''} onClick={() => setWebIndex(i)}>
                  {labelFor(PAGE_NAMES, p.page, isRtl)}{p.slot != null ? ` · ${labelFor(SLOT_NAMES, p.slot, isRtl)}` : ''}
                </button>
              ))}
            </div>
          )}
          <div className="promo-preview-lang-switch">
            <button type="button" className={webLang === 'en' ? 'active' : ''} onClick={() => setWebLang('en')}>EN</button>
            <button type="button" className={webLang === 'ar' ? 'active' : ''} onClick={() => setWebLang('ar')}>AR</button>
          </div>
          <BrowserChrome dir={webLang === 'ar' ? 'rtl' : 'ltr'}>
            {renderWebContent(type, webPlacement, webAd, newsBarIsScrolling)}
          </BrowserChrome>
        </div>
      )}

      {tab === 'mobile' && (
        <div className="promo-preview-body">
          {mobilePlacements.length > 1 && (
            <div className="promo-preview-placement-switch">
              {mobilePlacements.map((p, i) => (
                <button key={i} type="button" className={i === mobileIndex ? 'active' : ''} onClick={() => setMobileIndex(i)}>
                  {labelFor(PAGE_NAMES, p.page, true)}{p.slot != null ? ` · ${labelFor(SLOT_NAMES, p.slot, true)}` : ''}
                </button>
              ))}
            </div>
          )}
          <PhoneFrame>
            {renderMobileContent(type, mobilePlacement, mobileAd, newsBarIsScrolling)}
          </PhoneFrame>
        </div>
      )}

      {!hasWeb && !hasMobile && (
        <div className="promo-preview-fallback">
          <AdBlock
            ad={{
              title: isRtl ? titleAr : titleEn,
              description: isRtl ? descriptionAr : descriptionEn,
              ctaText: isRtl ? ctaTextAr : ctaTextEn,
              showCta,
              mediaUrl: mediaPreviewUrl,
              mediaType,
              rtl: isRtl,
            }}
            tall
          />
        </div>
      )}

      <style>{`
        .promo-preview-panel { background: var(--surface); border: 1px solid var(--border); border-radius: var(--r-lg); padding: var(--sp-4); }
        .promo-preview-tabs { display: flex; gap: var(--sp-2); margin-bottom: var(--sp-3); border-bottom: 1px solid var(--border-light); }
        .promo-preview-tabs button {
          padding: var(--sp-2) var(--sp-4);
          background: none; border: none; border-bottom: 2px solid transparent;
          font-weight: 600; color: var(--text-muted); cursor: pointer; font-size: var(--fs-sm);
          font-family: inherit;
        }
        .promo-preview-tabs button.active { color: var(--primary); border-bottom-color: var(--primary); }
        .promo-preview-tabs-empty { padding: var(--sp-2) 0; font-size: var(--fs-sm); color: var(--text-muted); }

        .promo-preview-placement-switch, .promo-preview-lang-switch { display: flex; flex-wrap: wrap; gap: var(--sp-1); margin-bottom: var(--sp-3); }
        .promo-preview-placement-switch button, .promo-preview-lang-switch button {
          padding: 4px 10px; border-radius: var(--r-pill); border: 1px solid var(--border);
          background: var(--surface); font-size: var(--fs-xs); cursor: pointer; color: var(--text-secondary);
          font-family: inherit;
        }
        .promo-preview-placement-switch button.active, .promo-preview-lang-switch button.active {
          background: var(--primary); color: #fff; border-color: var(--primary);
        }

        .ap-browser { border: 1px solid var(--border); border-radius: var(--r-lg); overflow: hidden; background: var(--surface); box-shadow: var(--shadow-sm); }
        .ap-browser-bar { display: flex; align-items: center; gap: 6px; padding: 8px 10px; background: var(--neutral-100); border-bottom: 1px solid var(--border); }
        .ap-dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }
        .ap-dot-red { background: #ff5f57; }
        .ap-dot-yellow { background: #febc2e; }
        .ap-dot-green { background: #28c840; }
        .ap-address { margin-inline-start: 10px; flex: 1; background: var(--surface); border: 1px solid var(--border); border-radius: var(--r-pill); padding: 3px 12px; font-size: 11px; color: var(--text-muted); }
        .ap-browser-body { max-height: 520px; overflow-y: auto; background: var(--bg); }

        .ap-phone { width: 280px; margin: 0 auto; border: 8px solid var(--neutral-800); border-radius: 32px; overflow: hidden; background: #000; box-shadow: var(--shadow-md); }
        .ap-phone-notch { width: 90px; height: 18px; background: var(--neutral-800); border-radius: 0 0 12px 12px; margin: 0 auto; }
        .ap-phone-status { display: flex; justify-content: space-between; padding: 4px 14px; font-size: 10px; color: #fff; background: #000; }
        .ap-phone-body { background: var(--bg); max-height: 460px; overflow-y: auto; }
        .ap-phone-tabbar { display: flex; justify-content: space-around; padding: 8px 0; background: var(--surface); border-top: 1px solid var(--border); }
        .ap-phone-tab { width: 20px; height: 20px; border-radius: 6px; background: var(--neutral-200); }

        .ap-chrome-header { height: 34px; background: var(--surface); border-bottom: 1px solid var(--border); }
        .ap-chrome-footer { height: 46px; background: var(--neutral-800); }
        .ap-mobile-appbar { display: flex; align-items: center; gap: 8px; padding: 10px 14px; background: var(--surface); border-bottom: 1px solid var(--border); }
        .ap-mobile-logo { width: 22px; height: 22px; border-radius: 50%; background: var(--primary-200); flex-shrink: 0; }
        .ap-mobile-title { font-size: 12px; font-weight: 700; color: var(--text); }
        .ap-mobile-search { margin: 8px 14px; height: 28px; border-radius: var(--r-lg); background: var(--surface); border: 1px solid var(--border); }

        .ap-sections { display: flex; flex-direction: column; }
        .ap-block { margin: 10px 14px; padding: 14px 10px; background: var(--neutral-100); border: 1px dashed var(--border); border-radius: var(--r-md); display: flex; align-items: center; justify-content: center; min-height: 44px; }
        .ap-block-tall { min-height: 88px; }
        .ap-block-label { font-size: 11px; color: var(--text-muted); font-weight: 600; text-align: center; }
        .ap-dimmed { opacity: 0.35; filter: grayscale(0.4); pointer-events: none; }

        .ap-promo-block { margin: 10px 14px; padding: 12px; background: var(--primary-50); border: 2px solid var(--primary-300); border-radius: var(--r-md); display: flex; flex-direction: column; align-items: center; text-align: center; gap: 6px; }
        .ap-promo-tag { font-size: 9px; font-weight: 700; letter-spacing: 0.03em; color: var(--primary); text-transform: uppercase; }
        .ap-promo-media { width: 100%; max-height: 110px; overflow: hidden; border-radius: var(--r-sm); background: var(--neutral-200); }
        .ap-promo-media img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .ap-promo-media-video { display: flex; align-items: center; justify-content: center; height: 80px; color: var(--text-muted); }
        .ap-promo-title { font-size: 13px; font-weight: 700; color: var(--text); }
        .ap-promo-desc { font-size: 11px; color: var(--text-secondary); }
        .ap-promo-cta { display: inline-block; padding: 5px 14px; background: var(--secondary); color: #fff; border-radius: var(--r-pill); font-size: 11px; font-weight: 700; }

        .ap-popup-wrap { position: relative; }
        .ap-popup-overlay { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(15,23,42,0.45); padding: 16px; }
        .ap-popup-card { position: relative; width: 100%; max-width: 240px; background: var(--surface); border-radius: var(--r-lg); box-shadow: var(--shadow-md); padding: 14px; display: flex; flex-direction: column; gap: 6px; align-items: center; text-align: center; }
        .ap-popup-close { position: absolute; top: 4px; inset-inline-end: 8px; font-size: 16px; color: var(--text-muted); }
        .ap-popup-title { font-weight: 700; font-size: 13px; color: var(--text); }
        .ap-popup-desc { font-size: 11px; color: var(--text-secondary); }

        .ap-newsbar { background: var(--secondary); color: #fff; overflow: hidden; }
        .ap-newsbar-track { display: flex; align-items: center; gap: 10px; padding: 6px 14px; white-space: nowrap; font-size: 11px; font-weight: 600; }
        .ap-newsbar-track.ap-marquee { animation: ap-marquee-scroll 8s linear infinite; width: max-content; }
        @keyframes ap-marquee-scroll { from { transform: translateX(100%); } to { transform: translateX(-100%); } }
        .ap-newsbar-cta { text-decoration: underline; }

        .promo-preview-fallback { padding: var(--sp-2) 0; }
      `}</style>
    </div>
  );
};

export default AdvertisementPreview;
